import React, { SyntheticEvent, useEffect, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import { compareDesc, isAfter, isValid } from 'date-fns'

import { Box, Tabs, Tab, Typography } from '@mui/material'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'

import NewReleasesIcon from '@mui/icons-material/NewReleases'
import useAuth from '../../../hooks/useAuth'
import { CompanyTargets } from './CompanyTargets'
import { PRTabs } from './PRTabs'
import { IsinMap } from './IsinMap/IsinMap'
import IsinsApi from '../../../api/IsinsApi'
import PRInputsApi from '../../../api/PRInputsApi'
import { SubscriptionButton } from './SubscriptionButton'
import useUpdatesSubscription from '../../../hooks/useUpdatesSubscription'
import { PRInputEntity } from '@finance-lab/utils/types'
import { CompanyEntity } from 'types/CompanyEntity'
import { IsinEntity } from '@finance-lab/utils/types'
import { SourceType } from 'types/SourceType'

const isinsApi = new IsinsApi()
const prinputsApi = new PRInputsApi()

type TabName = 'pr' | 'isin'

function PRInput() {
    const { isAuthenticated } = useAuth()
    const [tabValue, setTabValue] = useState<TabName>('pr')
    const [unusedIsins, setUnusedIsins] = useState<IsinEntity[]>([])
    const [filteredUnusedIsins, setFilteredUnusedIsins] = useState<IsinEntity[]>([])
    const [selectedTargets, setSelectedTargets] = useState([])
    const [sourceType, setSourceType] = useState<SourceType | null>(null)
    const [selectedCompany, setSelectedCompany] = useState<Pick<CompanyEntity, 'id' | 'name'> | null>(null)
    const [selectedPR, setSelectedPR] = useState<PRInputEntity | null>(null)
    const [prsInInbox, setPrsInInbox] = useState<Array<PRInputEntity> | null>(null)
    const [prsArchived, setPrsArchived] = useState<Array<PRInputEntity> | null>(null)
    const [isinStartDate, setIsinStartDate] = useState(new Date('2022-01-01'))
    const [onlyIsinsWithEvents, setOnlyIsinsWithEvents] = useState(false)
    const [newIsins, setNewIsins] = useState(false)

    const { clearCounter, updatesCount } = useUpdatesSubscription()

    const fetchPressReleases = React.useCallback(async () => {
        if (!isAuthenticated) return
        const result = (await prinputsApi.findAll()) || []

        const inbox = result.filter((pr) => !pr.archivedAt)
        const shortenedInbox = inbox.slice(0, 100)
        setPrsInInbox(shortenedInbox)
        const archived = result.filter((pr) => pr.archivedAt)
        setPrsArchived(archived.slice(0, 100))
        clearCounter()
    }, [isAuthenticated])

    useEffect(() => {
        void fetchPressReleases()
    }, [fetchPressReleases])

    useEffect(() => {
        if (updatesCount) {
            void fetchPressReleases()
        }
    }, [updatesCount, fetchPressReleases])

    useEffect(() => {
        if (!isAuthenticated) return
        const fetchData = async () => {
            const fetchedUnusedIsins = await isinsApi.fetchUnusedIsins()

            const startDate = new Date('2022-01-01')
            const newUnmatchedIsins = fetchedUnusedIsins.filter((unusedIsin) =>
                isAfter(new Date(unusedIsin.tradeFirstDate ?? 0), startDate),
            )

            setNewIsins(newUnmatchedIsins.length > 0)
            setUnusedIsins(fetchedUnusedIsins)
        }
        void fetchData()
    }, [isAuthenticated])

    useEffect(() => {
        if (isValid(isinStartDate) && isAfter(isinStartDate, new Date('2015-01-01'))) {
            const sortedAndFilteredUnusedIsins = unusedIsins
                .filter(
                    (unusedIsin) =>
                        isAfter(new Date(unusedIsin.tradeFirstDate ?? 0), isinStartDate) &&
                        (onlyIsinsWithEvents
                            ? unusedIsin.instrumentType === 'warrant'
                                ? unusedIsin.companyWarrants && unusedIsin.companyWarrants.length > 0
                                : unusedIsin.companyIssues && unusedIsin.companyIssues.length > 0
                            : true),
                )
                .sort((a, b) => compareDesc(new Date(a.tradeFirstDate ?? 0), new Date(b.tradeFirstDate ?? 0)))

            setFilteredUnusedIsins(sortedAndFilteredUnusedIsins)
        }
    }, [isinStartDate, onlyIsinsWithEvents, unusedIsins])

    const handleArchive = async (pr: PRInputEntity) => {
        await prinputsApi
            .archive(pr)
            .then(() => console.log(`Archived ${pr.id}`))
            .then(fetchPressReleases)
            .catch((error) => console.error(error))
    }

    const handleUnarchive = (pr: PRInputEntity) => {
        prinputsApi
            .unArchive(pr)
            .then(() => console.log(`Unarchived ${pr.id}`))
            .then(fetchPressReleases)
            .catch((error) => console.error(error))
    }

    const handleTabChange = (event: SyntheticEvent, newValue: TabName) => {
        setTabValue(newValue)
    }

    return (
        <>
            <Helmet title='PM Indata' />
            <Grid container>
                <Grid size={6}>
                    <Typography variant='h3' gutterBottom display='inline'>
                        PM Indata
                    </Typography>
                </Grid>
                <Grid size={6} sx={{ textAlign: 'right' }}>
                    <SubscriptionButton />
                </Grid>
            </Grid>

            <Box my={6} sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label='Pressmeddelanden' value={'pr'} />
                        <Tab
                            label={
                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <Typography alignSelf='flex-end'>ISIN</Typography>
                                    {newIsins ? <NewReleasesIcon color='warning' /> : null}
                                </Stack>
                            }
                            value={'isin'}
                        />
                    </Tabs>
                </Box>
            </Box>
            {tabValue === 'pr' ? (
                <Grid container spacing={6}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <PRTabs
                            prsInInbox={prsInInbox}
                            setPrsInInbox={setPrsInInbox}
                            prsArchived={prsArchived}
                            setPrsArchived={setPrsArchived}
                            setSelectedCompany={setSelectedCompany}
                            selectedPR={selectedPR}
                            setSelectedPR={setSelectedPR}
                            onArchive={handleArchive}
                            onUnarchive={handleUnarchive}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <CompanyTargets
                            selectedTargets={selectedTargets}
                            sourceType={sourceType}
                            setSourceType={setSourceType}
                            selectedCompany={selectedCompany}
                            selectedPR={selectedPR}
                            onArchivePR={handleArchive}
                        />
                    </Grid>
                </Grid>
            ) : null}
            {tabValue === 'isin' ? (
                <IsinMap
                    unusedIsins={filteredUnusedIsins}
                    setUnusedIsins={setFilteredUnusedIsins}
                    startDate={isinStartDate}
                    setStartDate={setIsinStartDate}
                    onlyIsinsWithEvents={onlyIsinsWithEvents}
                    setOnlyIsinsWithEvents={setOnlyIsinsWithEvents}
                />
            ) : null}
        </>
    )
}

export default PRInput
